/**
 * Yarn Collections Grid Carousel
 * Supports smooth slide animations, dynamic dot indicators, prev/next arrows, and touch/drag swiping.
 */

(function () {
  'use strict';

  function initYarnCollectionsGrid(root) {
    if (!root || root.dataset.ycgInit === 'true') return;
    root.dataset.ycgInit = 'true';

    const track = root.querySelector('[data-ycg-track]');
    if (!track) return;

    const cards = Array.from(track.querySelectorAll('.ycg-card'));
    const prevBtn = root.querySelector('[data-ycg-prev]');
    const nextBtn = root.querySelector('[data-ycg-next]');
    const dotsContainer = root.querySelector('[data-ycg-dots]');

    if (!cards.length) return;

    let dots = [];

    function getCardStep() {
      if (cards.length < 2) return track.offsetWidth;
      return cards[1].offsetLeft - cards[0].offsetLeft;
    }

    function getVisibleCardsCount() {
      const trackWidth = track.clientWidth;
      const cardWidth = cards[0].offsetWidth;
      if (cardWidth <= 0) return 4;
      return Math.max(1, Math.round(trackWidth / cardWidth));
    }

    function getMaxScrollIndex() {
      const maxScroll = track.scrollWidth - track.clientWidth;
      const step = getCardStep();
      if (maxScroll <= 4 || step <= 0) return 0;
      return Math.max(0, Math.round(maxScroll / step));
    }

    function renderDots() {
      if (!dotsContainer) return;
      dotsContainer.innerHTML = '';
      dots = [];

      const maxIndex = getMaxScrollIndex();
      const dotCount = maxIndex + 1;

      // If only 1 position exists (all cards fit in screen), hide dots
      if (dotCount <= 1) {
        dotsContainer.style.display = 'none';
        return;
      }
      dotsContainer.style.display = 'flex';

      for (let i = 0; i < dotCount; i++) {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = `ycg-dot${i === 0 ? ' is-active' : ''}`;
        dot.setAttribute('aria-label', `Go to slide ${i + 1}`);
        dot.dataset.index = i;
        dot.addEventListener('click', () => scrollToIndex(i));
        dotsContainer.appendChild(dot);
        dots.push(dot);
      }
    }

    function scrollToIndex(index) {
      if (!cards[index]) return;
      const step = getCardStep();
      const targetLeft = Math.min(index * step, track.scrollWidth - track.clientWidth);
      track.scrollTo({
        left: targetLeft,
        behavior: 'smooth'
      });
    }

    function updateControls() {
      const scrollLeft = track.scrollLeft;
      const maxScroll = track.scrollWidth - track.clientWidth;
      const step = getCardStep();
      const maxIndex = getMaxScrollIndex();

      let activeIdx = 0;
      if (maxScroll <= 4) {
        activeIdx = 0;
      } else if (scrollLeft >= maxScroll - 6) {
        activeIdx = maxIndex;
      } else if (step > 0) {
        activeIdx = Math.round(scrollLeft / step);
        activeIdx = Math.max(0, Math.min(activeIdx, maxIndex));
      }

      // Update dots
      if (dots.length) {
        dots.forEach((dot, idx) => {
          if (idx === activeIdx) {
            dot.classList.add('is-active');
          } else {
            dot.classList.remove('is-active');
          }
        });
      }

      // Update arrow disabled states
      if (prevBtn) {
        prevBtn.disabled = scrollLeft <= 4;
      }
      if (nextBtn) {
        nextBtn.disabled = scrollLeft >= maxScroll - 4;
      }
    }

    if (prevBtn) {
      prevBtn.addEventListener('click', () => {
        const step = getCardStep();
        track.scrollBy({ left: -step, behavior: 'smooth' });
      });
    }

    if (nextBtn) {
      nextBtn.addEventListener('click', () => {
        const step = getCardStep();
        track.scrollBy({ left: step, behavior: 'smooth' });
      });
    }

    // Scroll listener with requestAnimationFrame debounce
    let ticking = false;
    track.addEventListener('scroll', () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          updateControls();
          ticking = false;
        });
        ticking = true;
      }
    }, { passive: true });

    // Drag / Swipe support for desktop mouse
    let isDown = false;
    let startX = 0;
    let scrollStart = 0;

    track.addEventListener('mousedown', (e) => {
      isDown = true;
      startX = e.pageX - track.offsetLeft;
      scrollStart = track.scrollLeft;
      track.style.scrollBehavior = 'auto';
      track.style.scrollSnapType = 'none';
      track.style.cursor = 'grabbing';
    });

    window.addEventListener('mouseup', () => {
      if (!isDown) return;
      isDown = false;
      track.style.scrollBehavior = 'smooth';
      track.style.scrollSnapType = 'x mandatory';
      track.style.cursor = '';
      updateControls();
    });

    track.addEventListener('mousemove', (e) => {
      if (!isDown) return;
      e.preventDefault();
      const x = e.pageX - track.offsetLeft;
      const walk = (x - startX) * 1.2;
      track.scrollLeft = scrollStart - walk;
    });

    // Handle resize
    window.addEventListener('resize', () => {
      renderDots();
      updateControls();
    }, { passive: true });

    // Initial setup
    renderDots();
    setTimeout(updateControls, 100);
  }

  function initAll() {
    document.querySelectorAll('[data-ycg-root]').forEach(initYarnCollectionsGrid);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', function (event) {
    const root = event.target.querySelector('[data-ycg-root]') || (event.target.matches('[data-ycg-root]') ? event.target : null);
    if (root) initYarnCollectionsGrid(root);
  });
})();
